/**
 * Personalization tokens.
 *
 * Three kinds, all resolved to plain strings before render:
 *  - `{{first_name}}` — from a share link's audience or `?token[...]=`
 *  - `{{$M/d/yyyy|+30d}}` — a date with optional offset arithmetic
 *  - `{{fact:gross_margin_pct}}` — a governed number already on screen
 *
 * Two rules that matter more than they look:
 *  - **A token never renders as itself.** A prospect seeing literal
 *    `{{first_name}}` is worse than a generic greeting, so an unknown or empty
 *    token falls back to its declared default (and the validator requires one).
 *  - **Output is text, never HTML.** `renderTokenText` returns a string that
 *    callers put in a text node. There is no `dangerouslySetInnerHTML` path,
 *    because this content is authored by humans against prospect-supplied
 *    values.
 */

import type { TourAudience, TourTokenDecl } from './tourTypes'

const TOKEN_RE = /\{\{\s*([^}]+?)\s*\}\}/g

type DateUnit = 'd' | 'w' | 'm' | 'q' | 'y'

const UNIT_RE = /([+-]\d+)([dwmqy])/g

/** Apply `+5d` / `-2m` style offsets to a date. */
export function applyDateOffsets(base: Date, spec: string): Date {
  const d = new Date(base.getTime())
  for (const match of spec.matchAll(UNIT_RE)) {
    const amount = Number(match[1])
    const unit = match[2] as DateUnit
    if (!Number.isFinite(amount)) continue
    if (unit === 'd') d.setDate(d.getDate() + amount)
    else if (unit === 'w') d.setDate(d.getDate() + amount * 7)
    else if (unit === 'm') d.setMonth(d.getMonth() + amount)
    else if (unit === 'q') d.setMonth(d.getMonth() + amount * 3)
    else if (unit === 'y') d.setFullYear(d.getFullYear() + amount)
  }
  return d
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const pad = (n: number): string => String(n).padStart(2, '0')

/** Minimal date formatter — `d`, `dd`, `M`, `MM`, `MMM`, `MMMM`, `yy`, `yyyy`. */
export function formatDate(date: Date, pattern: string): string {
  return pattern.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d/g, (token) => {
    switch (token) {
      case 'yyyy': return String(date.getFullYear())
      case 'yy': return String(date.getFullYear()).slice(-2)
      case 'MMMM': return MONTHS[date.getMonth()]
      case 'MMM': return MONTHS[date.getMonth()].slice(0, 3)
      case 'MM': return pad(date.getMonth() + 1)
      case 'M': return String(date.getMonth() + 1)
      case 'dd': return pad(date.getDate())
      case 'd': return String(date.getDate())
      default: return token
    }
  })
}

export type TokenResolveOptions = {
  audience?: TourAudience | null
  tokens?: TourTokenDecl[] | null
  /** Governed values for `{{fact:*}}`, already formatted for display. */
  facts?: Record<string, string> | null
  /** Injectable clock so date tokens are testable. */
  now?: () => Date
}

/**
 * Substitute every token in `text`. Unresolvable tokens fall back to their
 * declared default, then to an empty string — never to their own braces.
 */
export function renderTokenText(text: string, options: TokenResolveOptions = {}): string {
  const defaults = new Map((options.tokens || []).map((t) => [t.key, t.defaultValue]))
  const audience = options.audience || {}
  const facts = options.facts || {}
  const now = options.now || (() => new Date())

  return String(text ?? '').replace(TOKEN_RE, (_full, rawInner: string) => {
    const raw = String(rawInner).trim()

    if (raw.startsWith('$')) {
      const [pattern, ...offsets] = raw.slice(1).split('|')
      const date = applyDateOffsets(now(), offsets.join(''))
      return formatDate(date, pattern.trim())
    }

    if (raw.startsWith('fact:')) {
      const key = raw.slice('fact:'.length).trim()
      return facts[key] ?? defaults.get(raw) ?? ''
    }

    const key = raw.split('|')[0].trim()
    const supplied = audience[key]
    if (typeof supplied === 'string' && supplied.trim()) return supplied.trim()
    return defaults.get(key) ?? ''
  })
}

/** Read Storylane-compatible `?token[first_name]=Jane` params into an audience. */
export function readAudienceFromSearch(search: string): TourAudience {
  const out: TourAudience = {}
  try {
    new URLSearchParams(search || '').forEach((value, key) => {
      const match = /^token\[([a-z0-9_-]+)\]$/i.exec(key)
      if (match) out[match[1]] = value
    })
  } catch {
    /* malformed query string — no personalization, not a failure */
  }
  return out
}
